import React, { useState, useEffect } from 'react';
import { auth } from '../config/firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import VerificationStatusSection from '../components/common/VerificationStatusSection';
import melodyCharacter from '../assets/melody-character.png';
import riffCharacter from '../assets/riff-character.png';

// Typewriter effect component for character dialogue
const TypewriterText = ({ text, speed = 50, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  
  useEffect(() => {
    if (!text) return;
    
    setDisplayedText('');
    setIsTyping(true);
    let currentIndex = 0;
    
    const typeTimer = setInterval(() => {
      if (currentIndex < text.length) {
        setDisplayedText(text.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typeTimer);
        if (onComplete) {
          onComplete();
        }
      }
    }, speed);
    
    return () => clearInterval(typeTimer);
  }, [text, speed, onComplete]);
  
  return (
    <span>
      {displayedText}
      {isTyping && (
        <span style={{
          animation: 'blink 1s infinite',
          marginLeft: '2px',
          color: '#10B981'
        }}>|</span>
      )}
    </span>
  );
};

const MusicianOnboarding = ({ user, onComplete, onSkip }) => {
  const [currentPhase, setCurrentPhase] = useState(1);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [formData, setFormData] = useState({
    realName: '',
    displayName: '',
    phoneNumber: '', // ✅ COLLECT ONLY - Don't verify during onboarding
    bio: '',
    genres: [],
    experience: '',
    songs: [],
    // ✅ NEW: Referral system fields
    referralCode: '',
    referrerEmail: ''
  });
  // ✅ NEW: Separate state for phone input
  const [phoneInput, setPhoneInput] = useState({
    country: 'AU', // Default to Australia
    localNumber: '' // Local format like 0414274979
  });
  const [newSong, setNewSong] = useState({ name: '', artist: '', price: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // ✅ NEW: Referral system state
  const [referralState, setReferralState] = useState({
    validating: false,
    validated: false,
    referrerInfo: null,
    error: null,
    method: 'code' // 'code' or 'email'
  });

  // ✅ NEW: Verification state for onboarding
  const [verificationState, setVerificationState] = useState({
    emailSent: false,
    emailVerified: false,
    phoneCodeSent: false,
    phoneVerified: false,
    phoneCode: '',
    isVerifying: false,
    pollCount: 0,
    pollInterval: null
  });

  const functions = getFunctions();

  // ✅ NEW: Country data with phone codes and formatting info
  const countries = {
    AU: {
      name: 'Australia',
      flag: '🇦🇺',
      code: '+61',
      format: '0XXX XXX XXX',
      placeholder: '0414 123 456',
      pattern: /^0[1-9]\d{8}$/,
      example: '0414144979'
    },
    US: {
      name: 'United States',
      flag: '🇺🇸',
      code: '+1',
      format: '(XXX) XXX-XXXX',
      placeholder: '(555) 123-4567',
      pattern: /^\([0-9]{3}\)\s?[0-9]{3}-[0-9]{4}$|^[0-9]{10}$/,
      example: '5551234567'
    },
    GB: {
      name: 'United Kingdom',
      flag: '🇬🇧',
      code: '+44',
      format: '0XXXX XXXXXX',
      placeholder: '07700 900123',
      pattern: /^0[1-9]\d{9,10}$/,
      example: '07700900123'
    },
    CA: {
      name: 'Canada',
      flag: '🇨🇦',
      code: '+1',
      format: '(XXX) XXX-XXXX',
      placeholder: '(555) 123-4567',
      pattern: /^\([0-9]{3}\)\s?[0-9]{3}-[0-9]{4}$|^[0-9]{10}$/,
      example: '5551234567'
    },
    NZ: {
      name: 'New Zealand',
      flag: '🇳🇿',
      code: '+64',
      format: '0XX XXX XXXX',
      placeholder: '021 123 4567',
      pattern: /^0[1-9]\d{7,9}$/,
      example: '0211234567'
    }
  };

  // ✅ NEW: Convert local number to international format
  const convertToInternationalFormat = (country, localNumber) => {
    if (!localNumber.trim()) return '';
    
    const countryData = countries[country];
    if (!countryData) return localNumber;

    let cleanNumber = localNumber.replace(/[\s\-\(\)]/g, ''); // Remove formatting
    
    // Handle different country conversions
    switch (country) {
      case 'AU':
        // Convert 0414434979 to +61414434979
        if (cleanNumber.startsWith('0')) {
          cleanNumber = cleanNumber.substring(1);
        }
        return `${countryData.code}${cleanNumber}`;
      
      case 'US':
      case 'CA':
        // Convert 5551234567 to +15551234567
        return `${countryData.code}${cleanNumber}`;
      
      case 'GB':
        // Convert 07700900123 to +447700900123
        if (cleanNumber.startsWith('0')) {
          cleanNumber = cleanNumber.substring(1);
        }
        return `${countryData.code}${cleanNumber}`;
      
      case 'NZ':
        // Convert 0211234567 to +64211234567
        if (cleanNumber.startsWith('0')) {
          cleanNumber = cleanNumber.substring(1);
        }
        return `${countryData.code}${cleanNumber}`;
      
      default:
        return `${countryData.code}${cleanNumber}`;
    }
  };

  // ✅ NEW: Validate local phone number format
  const validateLocalPhoneNumber = (country, localNumber) => {
    if (!localNumber.trim()) return true; // Optional field
    
    const countryData = countries[country];
    if (!countryData) return false;
    
    const cleanNumber = localNumber.replace(/[\s\-\(\)]/g, '');
    return countryData.pattern.test(cleanNumber);
  };

  // ✅ NEW: Referral validation functions
  const validateReferralCode = async (code) => {
    if (!code.trim()) {
      setReferralState(prev => ({ ...prev, validated: false, referrerInfo: null, error: null }));
      return;
    }

    setReferralState(prev => ({ ...prev, validating: true, error: null }));

    try {
      const validateReferralCode = httpsCallable(functions, 'validateReferralCode');
      const result = await validateReferralCode({ referralCode: code });

      if (result.data.valid) {
        setReferralState(prev => ({
          ...prev,
          validating: false,
          validated: true,
          referrerInfo: result.data.referrer,
          error: null
        }));
      } else {
        setReferralState(prev => ({
          ...prev,
          validating: false,
          validated: false,
          referrerInfo: null,
          error: result.data.error || 'Invalid referral code'
        }));
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setReferralState(prev => ({
        ...prev,
        validating: false,
        validated: false,
        referrerInfo: null,
        error: 'Failed to validate referral code'
      }));
    }
  };


  // ADDED: Scroll to top when phase changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPhase]);

  // ✅ UPDATED: Update phone number when country or local number changes
  useEffect(() => {
    const internationalNumber = convertToInternationalFormat(phoneInput.country, phoneInput.localNumber);
    setFormData(prev => ({ ...prev, phoneNumber: internationalNumber }));
  }, [phoneInput.country, phoneInput.localNumber]);

  // ✅ NEW: Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (verificationState.pollInterval) {
        clearInterval(verificationState.pollInterval);
      }
    };
  }, [verificationState.pollInterval]);

  const characters = {
    melody: {
      name: "Melody",
      personality: "energetic",
      image: melodyCharacter,
      color: "#10B981",
      description: "Your energetic music guide!",
      messages: {
        welcome: "Hey rockstar! 🎸 I'm Melody, and I'm PUMPED to help you set up your RequestASong profile! This is going to be AMAZING!",
        basicInfo: "Alright superstar! Let's get your basic info so fans can find you and fall in love with your music! ✨",
        songs: "TIME FOR THE FUN PART! 🎵 Let's add your songs so fans can request them! The more songs, the more requests, the more money! 💰",
        phoneCollection: "Almost done! 📱 Choose your country and add your phone number as you know it - no need for country codes!"
      }
    },
    riff: {
      name: "Riff", 
      personality: "edgy",
      image: riffCharacter,
      color: "#7C3AED",
      description: "Your rock-solid music companion!",
      messages: {
        welcome: "What's up, musician? 🤘 Name's Riff. I'm here to help you get this setup done right. No fluff, just results.",
        basicInfo: "Alright, let's get down to business. Need your basic info so people can find you and throw money at you for music. Simple.",
        songs: "Time to build your setlist. 🎸 Add the songs you actually want to play - with prices that make it worth your time.",
        phoneCollection: "Final step - pick your country and enter your number the way you normally write it. We'll handle the rest."
      }
    }
  };

  const genres = [
    "Pop", "Rock", "Jazz", "Blues", "Country", "Folk", "R&B", "Hip-Hop", 
    "Electronic", "Classical", "Indie", "Alternative", "Reggae", "Latin",
    "Punk", "Metal", "Acoustic", "Singer-Songwriter"
  ];

  const experiences = [
    "Just starting out (0-1 years)",
    "Getting comfortable (1-2 years)", 
    "Building my repertoire (2-5 years)",
    "Experienced performer (5+ years)",
    "Professional musician (10+ years)",
    "Seasoned veteran (I've seen it all!)"
  ];

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const isTablet = typeof window !== 'undefined' && window.innerWidth < 768;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ✅ NEW: Handle phone input changes
  const handlePhoneInputChange = (field, value) => {
    setPhoneInput(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre) 
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  const addSong = () => {
    if (!newSong.name.trim() || !newSong.artist.trim()) {
      alert("Please enter both song name and artist");
      return;
    }
    
    const price = parseFloat(newSong.price);
    if (isNaN(price) || price < 0) {
      alert("Please enter a valid price (minimum $0.50)");
      return;
    }

    if (price < 0.5) {
      alert("Minimum price is $0.50");
      return;
    }

    setFormData(prev => ({
      ...prev,
      songs: [...prev.songs, { 
        ...newSong, 
        price, 
        id: Date.now(),
        createdAt: new Date()
      }]
    }));
    
    setNewSong({ name: '', artist: '', price: '' });
  };

  const removeSong = (id) => {
    setFormData(prev => ({
      ...prev,
      songs: prev.songs.filter(song => song.id !== id)
    }));
  };

  const editSong = (id, updatedSong) => {
    setFormData(prev => ({
      ...prev,
      songs: prev.songs.map(song => 
        song.id === id ? { ...song, ...updatedSong } : song
      )
    }));
  };

  const validatePhase1 = () => {
    if (!selectedCharacter) {
      alert("Please select a character guide first!");
      return false;
    }
    if (!formData.realName.trim()) {
      alert("Please enter your real name!");
      return false;
    }
    if (!formData.displayName.trim()) {
      alert("Please enter your stage name!");
      return false;
    }
    return true;
  };

  const validatePhase2 = () => {
    if (formData.songs.length === 0) {
      alert("Please add at least one song that fans can request!");
      return false;
    }
    return true;
  };

  // ✅ UPDATED: Validate phone number using new format validation
  const validatePhase3 = () => {
    if (phoneInput.localNumber.trim() && !validateLocalPhoneNumber(phoneInput.country, phoneInput.localNumber)) {
      const countryData = countries[phoneInput.country];
      alert(`Please enter a valid ${countryData.name} phone number. Example: ${countryData.example}`);
      return false;
    }
    return true;
  };

  // ✅ NEW: Verification functions for onboarding
  const sendEmailVerification = async () => {
    try {
      setVerificationState(prev => ({ ...prev, isVerifying: true }));
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        alert('Please sign in to verify your email.');
        return;
      }

      // Use clean email verification (no earnings text)
      const sendEmailVerification = httpsCallable(functions, 'sendEmailVerification');
      const result = await sendEmailVerification();
      
      if (!result.data.success) {
        throw new Error(result.data.message);
      }
      
      setVerificationState(prev => ({ 
        ...prev, 
        emailSent: true, 
        isVerifying: false,
        pollCount: 0 
      }));
      
      // Start polling 3 times at 10-second intervals
      startEmailPolling();
      
    } catch (error) {
      console.error('Email verification error:', error);
      setVerificationState(prev => ({ ...prev, isVerifying: false }));
      
      if (error.code === 'auth/email-already-verified') {
        setVerificationState(prev => ({ ...prev, emailVerified: true }));
      } else if (error.code === 'auth/too-many-requests') {
        alert('Too many requests. Please wait a few minutes before trying again.');
      } else {
        alert('Failed to send verification email: ' + error.message);
      }
    }
  };

  const startEmailPolling = () => {
    const interval = setInterval(async () => {
      setVerificationState(prev => {
        if (prev.pollCount >= 3) {
          clearInterval(interval);
          return prev;
        }
        
        checkEmailVerification();
        return { ...prev, pollCount: prev.pollCount + 1 };
      });
    }, 10000); // 10 seconds
    
    setVerificationState(prev => ({ ...prev, pollInterval: interval }));
  };

  const checkEmailVerification = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await currentUser.reload();
        if (currentUser.emailVerified) {
          // ✅ CRITICAL FIX: Call backend to sync Firestore with Firebase Auth
          try {
            const checkEmailVerification = httpsCallable(functions, 'checkEmailVerification');
            await checkEmailVerification();
            console.log('✅ Email verification synced with backend');
          } catch (backendError) {
            console.error('❌ Failed to sync email verification with backend:', backendError);
          }
          
          setVerificationState(prev => {
            if (prev.pollInterval) {
              clearInterval(prev.pollInterval);
            }
            return { ...prev, emailVerified: true, pollInterval: null };
          });
        }
      }
    } catch (error) {
      console.error('Error checking email verification:', error);
    }
  };

  const sendPhoneVerification = async () => {
    if (!formData.phoneNumber.trim()) {
      alert('Please enter your phone number first');
      return;
    }

    try {
      setVerificationState(prev => ({ ...prev, isVerifying: true }));
      
      const sendPhoneVerification = httpsCallable(functions, 'sendPhoneVerification');
      const result = await sendPhoneVerification({
        phoneNumber: formData.phoneNumber
      });
      
      if (result.data.success) {
        setVerificationState(prev => ({ 
          ...prev, 
          phoneCodeSent: true, 
          isVerifying: false 
        }));
      }
      
    } catch (error) {
      console.error('Phone verification error:', error);
      setVerificationState(prev => ({ ...prev, isVerifying: false }));
      
      if (error.code === 'functions/resource-exhausted') {
        alert('Too many requests. Please wait a few minutes before trying again.');
      } else {
        alert('Failed to send SMS code: ' + error.message);
      }
    }
  };

  const verifyPhoneCode = async () => {
    try {
      setVerificationState(prev => ({ ...prev, isVerifying: true }));
      
      const verifyPhoneCode = httpsCallable(functions, 'verifyPhoneCode');
      const result = await verifyPhoneCode({
        code: verificationState.phoneCode
      });
      
      if (result.data.success) {
        setVerificationState(prev => ({ 
          ...prev, 
          phoneVerified: true, 
          isVerifying: false 
        }));
      }
      
    } catch (error) {
      console.error('Phone code verification error:', error);
      setVerificationState(prev => ({ ...prev, isVerifying: false }));
      alert('Invalid code: ' + error.message);
    }
  };

  // ✅ NEW: Start both verifications simultaneously
  const startBothVerifications = async () => {
    if (!formData.phoneNumber.trim()) {
      alert('Please enter your phone number first');
      return;
    }

    // Start both simultaneously
    await Promise.all([
      sendEmailVerification(),
      sendPhoneVerification()
    ]);
  };

  const handleComplete = async () => {
    if (!validatePhase1() || !validatePhase2() || !validatePhase3()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const completeOnboardingV2 = httpsCallable(functions, 'completeOnboardingV2');
      
      console.log("Completing onboarding with data:", {
        ...formData,
        selectedCharacter,
        totalSongs: formData.songs.length,
        phoneFormatted: formData.phoneNumber // This is now the international format
      });
      
      const result = await completeOnboardingV2({
        realName: formData.realName.trim(),
        displayName: formData.displayName.trim(),
        phoneNumber: formData.phoneNumber.trim(), // International format
        bio: formData.bio.trim(),
        genres: formData.genres,
        experience: formData.experience,
        selectedCharacter,
        songs: formData.songs.map(song => ({
          name: song.name.trim(),
          artist: song.artist.trim(),
          price: parseFloat(song.price),
          createdAt: song.createdAt || new Date()
        })),
        skipPhoneVerification: true
      });

      if (result.data.success) {
        console.log("Onboarding completed successfully");
        
        // ✅ NEW: Record referral if provided
        if (referralState.validated && referralState.referrerInfo) {
          try {
            console.log("Recording referral:", referralState.referrerInfo);
            const recordReferral = httpsCallable(functions, 'recordReferral');
            
            // Get device fingerprint for fraud detection
            const deviceFingerprint = navigator.userAgent + navigator.language + window.screen.width + window.screen.height;
            
            await recordReferral({
              referralCode: referralState.method === 'code' ? formData.referralCode : null,
              referrerEmail: referralState.method === 'email' ? formData.referrerEmail : null,
              deviceFingerprint: btoa(deviceFingerprint), // Base64 encode for privacy
              userAgent: navigator.userAgent,
              ipAddress: null // Will be detected server-side if needed
            });
            
            console.log("Referral recorded successfully");
          } catch (referralError) {
            console.warn("Failed to record referral (non-critical):", referralError);
            // Don't fail onboarding if referral recording fails
          }
        }
        
        onComplete({
          realName: formData.realName.trim(),
          displayName: formData.displayName.trim(),
          phoneNumber: formData.phoneNumber.trim(),
          bio: formData.bio.trim(),
          genres: formData.genres,
          experience: formData.experience,
          selectedCharacter,
          songs: formData.songs,
          onboardingCompleted: true,
          phoneVerified: false,
          canReceiveRequests: true,
          // ✅ NEW: Include referral info
          referralInfo: referralState.validated ? referralState.referrerInfo : null
        });
      } else {
        throw new Error(result.data.message || "Failed to complete onboarding");
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      alert("Error completing setup: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedChar = selectedCharacter ? characters[selectedCharacter] : null;
  const selectedCountry = countries[phoneInput.country];

  // Container styles (same as before)
  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#111827',
    color: 'white',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    overflow: 'hidden',
    paddingTop: isMobile ? '32px' : '48px'
  };

  const headerStyle = {
    backgroundColor: '#1F2937',
    borderBottom: '8px solid #7C3AED',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    position: 'sticky',
    top: 0,
    zIndex: 50
  };

  const cardStyle = {
    backgroundColor: '#1F2937',
    border: '8px solid #7C3AED',
    borderRadius: '24px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    marginBottom: '32px'
  };

  const inputStyle = {
    width: '100%',
    backgroundColor: '#374151',
    border: '4px solid #4B5563',
    borderRadius: '16px',
    padding: '16px 20px',
    color: 'white',
    fontSize: '18px',
    fontWeight: '500',
    outline: 'none',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    backgroundColor: '#7C3AED',
    color: 'white',
    fontWeight: 'bold',
    padding: '16px 32px',
    borderRadius: '16px',
    border: '4px solid #6D28D9',
    fontSize: '18px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px'
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          
          @keyframes slideIn {
            from { transform: translateX(-20px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
          
          .bounce { animation: bounce 2s infinite; }
          .pulse { animation: pulse 2s infinite; }
          .slide-in { animation: slideIn 0.5s ease-out; }
          
          .character-card:hover {
            transform: scale(1.02);
            box-shadow: 0 20px 40px rgba(124, 58, 237, 0.3);
          }
          
          .song-card {
            transition: all 0.3s ease;
          }
          
          .song-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
          }
          
          input:focus, textarea:focus, select:focus {
            border-color: #7C3AED !important;
            box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
          }
          
          .genre-tag {
            transition: all 0.2s ease;
          }
          
          .genre-tag:hover {
            transform: scale(1.05);
          }
          
          .country-option {
            transition: all 0.2s ease;
          }
          
          .country-option:hover {
            background-color: #4B5563 !important;
          }
          
          @media (max-width: 640px) {
            .mobile-stack {
              flex-direction: column;
            }
            
            .mobile-full {
              width: 100%;
            }
          }
        `}
      </style>

      {/* Fixed Header - Same as before */}
      <div style={headerStyle}>
        <div style={{ 
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: isMobile ? '20px 16px' : '32px 32px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '0'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '20px'
            }}>
              <div style={{
                width: isMobile ? '60px' : '80px',
                height: isMobile ? '60px' : '80px',
                backgroundColor: '#7C3AED',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '6px solid #6D28D9',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
              }}>
                <span style={{ fontSize: isMobile ? '28px' : '36px' }} className="bounce">🎸</span>
              </div>
              <div>
                <h1 style={{ 
                  fontSize: isMobile ? '24px' : '36px', 
                  fontWeight: 'bold', 
                  margin: '0 0 8px 0',
                  background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Welcome to RequestASong! 
                </h1>
                <p style={{ 
                  color: '#C4B5FD', 
                  fontSize: isMobile ? '14px' : '18px', 
                  margin: 0,
                  fontWeight: '500'
                }}>
                  Let's get you set up to start earning! 💰
                </p>
              </div>
            </div>
            
            <button
              onClick={onSkip}
              style={{
                backgroundColor: 'rgba(107, 114, 128, 0.8)',
                color: '#D1D5DB',
                fontWeight: '600',
                padding: isMobile ? '12px 20px' : '16px 24px',
                borderRadius: '12px',
                border: '2px solid #6B7280',
                fontSize: isMobile ? '14px' : '16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = 'rgba(75, 85, 99, 0.9)';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'rgba(107, 114, 128, 0.8)';
                e.target.style.color = '#D1D5DB';
              }}
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar - Same as before */}
      <div style={{ backgroundColor: '#1F2937', padding: isMobile ? '16px' : '24px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: '16px'
          }}>
            <span style={{ 
              fontSize: isMobile ? '14px' : '16px', 
              color: '#9CA3AF',
              fontWeight: '600'
            }}>
              Step {currentPhase} of 4: {
                currentPhase === 1 ? 'Profile Setup' :
                currentPhase === 2 ? 'Add Songs' :
                currentPhase === 3 ? 'Contact Info' :
                'Phone Verification'
              }
            </span>
            <span style={{ 
              fontSize: isMobile ? '14px' : '16px', 
              color: '#9CA3AF',
              fontWeight: '600'
            }}>
              {Math.round((currentPhase / 4) * 100)}% Complete
            </span>
          </div>
          <div style={{
            width: '100%',
            backgroundColor: '#374151',
            borderRadius: '12px',
            height: '12px',
            border: '2px solid #4B5563',
            overflow: 'hidden'
          }}>
            <div 
              style={{
                backgroundColor: '#7C3AED',
                height: '100%',
                borderRadius: '8px',
                transition: 'width 0.5s ease',
                width: `${(currentPhase / 3) * 100}%`,
                background: 'linear-gradient(90deg, #7C3AED, #A78BFA)',
                boxShadow: '0 2px 4px rgba(124, 58, 237, 0.4)'
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: isMobile ? '24px 16px' : '48px 32px',
        minHeight: 'calc(100vh - 200px)'
      }}>
        <>
        {/* Phase 1 & 2 remain exactly the same as before... */}
        {/* Phase 1: Character Selection & Basic Info */}
        {currentPhase === 1 && (
          <div className="slide-in">

            {/* Character selection and basic info form - keeping exactly the same as original */}
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h2 style={{ 
                fontSize: isMobile ? '32px' : '48px', 
                fontWeight: 'bold', 
                margin: '0 0 16px 0',
                background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Choose Your Music Guide! 🎵
              </h2>
              <p style={{ 
                fontSize: isMobile ? '16px' : '24px', 
                color: '#9CA3AF',
                fontWeight: '500',
                margin: 0
              }}>
                Pick a character to help you through your musical journey
              </p>
            </div>

            {/* Character Selection */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '32px',
              marginBottom: '48px'
            }}>
              {Object.entries(characters).map(([key, char]) => (
                <div
                  key={key}
                  onClick={() => setSelectedCharacter(key)}
                  className="character-card"
                  style={{
                    position: 'relative',
                    padding: '32px',
                    borderRadius: '24px',
                    border: selectedCharacter === key 
                      ? '6px solid white' 
                      : '6px solid #4B5563',
                    backgroundColor: selectedCharacter === key 
                      ? '#374151' 
                      : '#1F2937',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: selectedCharacter === key ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: selectedCharacter === key 
                      ? '0 25px 50px rgba(124, 58, 237, 0.3)' 
                      : '0 10px 25px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: isMobile ? '120px' : '160px',
                      height: isMobile ? '120px' : '160px',
                      margin: '0 auto 24px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '6px solid #6B7280',
                      backgroundColor: char.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <img 
                        src={char.image} 
                        alt={char.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        imageRendering: 'crisp-edges',
                        transform: 'translateZ(0)'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <span 
                        style={{ 
                          fontSize: isMobile ? '48px' : '64px',
                          display: 'none'
                        }}
                      >
                        🎤
                      </span>
                    </div>
                    
                    <h3 style={{ 
                      fontSize: isMobile ? '24px' : '32px', 
                      fontWeight: 'bold', 
                      margin: '0 0 12px 0',
                      color: char.color
                    }}>
                      {char.name}
                    </h3>
                    
                    <p style={{ 
                      color: '#D1D5DB', 
                      fontSize: isMobile ? '16px' : '18px',
                      margin: '0 0 16px 0',
                      fontWeight: '500'
                    }}>
                      {char.description}
                    </p>
                    
                    <div style={{
                      backgroundColor: selectedCharacter === key ? char.color : '#4B5563',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '12px',
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease'
                    }}>
                      {selectedCharacter === key ? '✓ Selected!' : 'Select Me!'}
                    </div>

                    {selectedCharacter === key && (
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#10B981',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '3px solid white',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                      }}>
                        <span style={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>✓</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Character Message & Basic Info Form */}
            {selectedCharacter && (
              <div style={cardStyle}>
                <div style={{
                  padding: '32px',
                  borderBottom: '4px solid #6D28D9',
                  backgroundColor: '#581C87'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '20px',
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <div style={{
                      width: isMobile ? '80px' : '120px',
                      height: isMobile ? '80px' : '120px',
                      borderRadius: '50%',
                      backgroundColor: selectedChar.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '4px solid white',
                      flexShrink: 0,
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={selectedChar.image} 
                        alt={selectedChar.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        imageRendering: 'crisp-edges',
                        transform: 'translateZ(0)'
                        }}
                      />
                    </div>
                    
                    <div style={{
                      backgroundColor: '#374151',
                      borderRadius: '20px',
                      padding: '20px',
                      flex: 1,
                      position: 'relative',
                      border: '3px solid #4B5563'
                    }}>
                      {!isMobile && (
                        <div style={{
                          position: 'absolute',
                          left: '-12px',
                          top: '24px',
                          width: 0,
                          height: 0,
                          borderTop: '12px solid transparent',
                          borderBottom: '12px solid transparent',
                          borderRight: '12px solid #374151'
                        }}></div>
                      )}
                      
                      <p style={{ 
                        color: 'white', 
                        fontSize: isMobile ? '16px' : '18px',
                        margin: 0,
                        lineHeight: '1.5',
                        fontWeight: '500'
                      }}>
                        <TypewriterText 
                          text={selectedChar.messages.basicInfo} 
                          speed={30}
                          key={`basicInfo-${selectedCharacter}`}
                        />
                      </p>
                    </div>
                  </div>
                </div>
                
                <div style={{ padding: isMobile ? '24px' : '40px' }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: '32px',
                    marginBottom: '32px'
                  }}>
                    {/* Show the user's email */}
                    <div style={{ marginBottom: '32px' }}>
                      <label style={{ 
                        display: 'block', 
                        color: '#C4B5FD', 
                        fontWeight: 'bold', 
                        fontSize: '18px', 
                        marginBottom: '12px'
                      }}>
                        Email Address
                      </label>
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#1F2937',
                        border: '2px solid #374151',
                        borderRadius: '8px',
                        color: '#D1D5DB',
                        fontSize: '16px'
                      }}>
                        {user?.email || 'No email available'}
                      </div>
                      <p style={{ 
                        color: '#9CA3AF', 
                        fontSize: '14px', 
                        margin: '8px 0 0 0',
                        fontStyle: 'italic'
                      }}>
                        📧 Your account email (used for notifications & verification)
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block', 
                        color: '#C4B5FD', 
                        fontWeight: 'bold', 
                        fontSize: '18px', 
                        marginBottom: '12px'
                      }}>
                        Real Name *
                      </label>
                      <input
                        type="text"
                        value={formData.realName}
                        onChange={(e) => handleInputChange('realName', e.target.value)}
                        placeholder="Your full legal name"
                        style={inputStyle}
                      />
                      <p style={{ 
                        color: '#9CA3AF', 
                        fontSize: '14px', 
                        margin: '8px 0 0 0',
                        fontStyle: 'italic'
                      }}>
                        🔒 For our records only - never shown publicly
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block', 
                        color: '#C4B5FD', 
                        fontWeight: 'bold', 
                        fontSize: '18px', 
                        marginBottom: '12px'
                      }}>
                        Stage Name *
                      </label>
                      <input
                        type="text"
                        value={formData.displayName}
                        onChange={(e) => handleInputChange('displayName', e.target.value)}
                        placeholder="Your stage name (e.g., 'Acoustic Mike')"
                        style={inputStyle}
                      />
                      <p style={{ 
                        color: '#9CA3AF', 
                        fontSize: '14px', 
                        margin: '8px 0 0 0',
                        fontStyle: 'italic'
                      }}>
                        This is how fans will see you
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '32px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: '#C4B5FD', 
                      fontWeight: 'bold', 
                      fontSize: '18px', 
                      marginBottom: '12px'
                    }}>
                      Bio (Optional)
                    </label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="Tell fans about your musical style, experience, favorite songs to play..."
                      rows="4"
                      style={{
                        ...inputStyle,
                        resize: 'vertical',
                        minHeight: '120px'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '32px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: '#C4B5FD', 
                      fontWeight: 'bold', 
                      fontSize: '18px', 
                      marginBottom: '16px'
                    }}>
                      Music Genres (Select all that apply)
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '12px'
                    }}>
                      {genres.map(genre => (
                        <button
                          key={genre}
                          onClick={() => handleGenreToggle(genre)}
                          className="genre-tag"
                          style={{
                            padding: '12px 20px',
                            borderRadius: '24px',
                            fontSize: '16px',
                            fontWeight: '600',
                            border: '3px solid',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            backgroundColor: formData.genres.includes(genre)
                              ? '#7C3AED'
                              : 'transparent',
                            color: formData.genres.includes(genre)
                              ? 'white'
                              : '#D1D5DB',
                            borderColor: formData.genres.includes(genre)
                              ? '#7C3AED'
                              : '#6B7280'
                          }}
                        >
                          {genre}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '32px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: '#C4B5FD', 
                      fontWeight: 'bold', 
                      fontSize: '18px', 
                      marginBottom: '12px'
                    }}>
                      Experience Level
                    </label>
                    <select
                      value={formData.experience}
                      onChange={(e) => handleInputChange('experience', e.target.value)}
                      style={{
                        ...inputStyle,
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Select your experience level...</option>
                      {experiences.map(exp => (
                        <option key={exp} value={exp} style={{ backgroundColor: '#374151', color: 'white' }}>
                          {exp}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '40px' }}>
              {selectedCharacter && formData.realName && formData.displayName && (
                <button
                  onClick={() => validatePhase1() && setCurrentPhase(2)}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#10B981',
                    borderColor: '#059669',
                    fontSize: '20px',
                    padding: '20px 40px',
                    boxShadow: '0 15px 30px rgba(16, 185, 129, 0.4)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#059669';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#10B981';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <span>🎵</span>
                  Next: Add Your Songs
                </button>
              )}
            </div>
          </div>
        )}

        {/* Phase 2: Add Songs - keeping exactly the same */}
        {currentPhase === 2 && (
          <div className="slide-in">
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h2 style={{ 
                fontSize: isMobile ? '32px' : '48px', 
                fontWeight: 'bold', 
                margin: '0 0 16px 0',
                background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Add Your Songs 🎵
              </h2>
              <p style={{ 
                fontSize: isMobile ? '16px' : '24px', 
                color: '#9CA3AF',
                fontWeight: '500',
                margin: 0
              }}>
                Build your requestable repertoire - this is how you'll earn money!
              </p>
            </div>

            {selectedChar && (
              <div style={cardStyle}>
                <div style={{
                  padding: '32px',
                  borderBottom: '4px solid #6D28D9',
                  backgroundColor: '#581C87'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '20px',
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <div style={{
                      width: isMobile ? '80px' : '120px',
                      height: isMobile ? '80px' : '120px',
                      borderRadius: '50%',
                      backgroundColor: selectedChar.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '4px solid white',
                      flexShrink: 0,
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={selectedChar.image} 
                        alt={selectedChar.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        imageRendering: 'crisp-edges',
                        transform: 'translateZ(0)'
                        }}
                      />
                    </div>
                    
                    <div style={{
                      backgroundColor: '#374151',
                      borderRadius: '20px',
                      padding: '20px',
                      flex: 1,
                      position: 'relative',
                      border: '3px solid #4B5563'
                    }}>
                      {!isMobile && (
                        <div style={{
                          position: 'absolute',
                          left: '-12px',
                          top: '24px',
                          width: 0,
                          height: 0,
                          borderTop: '12px solid transparent',
                          borderBottom: '12px solid transparent',
                          borderRight: '12px solid #374151'
                        }}></div>
                      )}
                      
                      <p style={{ 
                        color: 'white', 
                        fontSize: isMobile ? '16px' : '18px',
                        margin: 0,
                        lineHeight: '1.5',
                        fontWeight: '500'
                      }}>
                        <TypewriterText 
                          text={selectedChar.messages.songs} 
                          speed={30}
                          key={`songs-${selectedCharacter}`}
                        />
                      </p>
                    </div>
                  </div>
                </div>
                
                <div style={{ padding: isMobile ? '24px' : '40px' }}>
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '20px',
                    padding: '32px',
                    border: '4px solid #4B5563',
                    marginBottom: '40px'
                  }}>
                    <h3 style={{ 
                      color: '#C4B5FD', 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      margin: '0 0 24px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <span>➕</span>
                      Add New Song
                    </h3>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isMobile 
                        ? '1fr' 
                        : isTablet 
                          ? '1fr 1fr' 
                          : '2fr 2fr 1fr auto',
                      gap: '20px',
                      alignItems: 'end'
                    }}>
                      <div>
                        <label style={{ 
                          display: 'block', 
                          color: '#C4B5FD', 
                          fontWeight: 'bold', 
                          marginBottom: '8px'
                        }}>
                          Song Name *
                        </label>
                        <input
                          type="text"
                          value={newSong.name}
                          onChange={(e) => setNewSong(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Enter song name"
                          style={inputStyle}
                        />
                      </div>
                      
                      <div>
                        <label style={{ 
                          display: 'block', 
                          color: '#C4B5FD', 
                          fontWeight: 'bold', 
                          marginBottom: '8px'
                        }}>
                          Artist *
                        </label>
                        <input
                          type="text"
                          value={newSong.artist}
                          onChange={(e) => setNewSong(prev => ({ ...prev, artist: e.target.value }))}
                          placeholder="Enter artist name"
                          style={inputStyle}
                        />
                      </div>
                      
                      <div>
                        <label style={{ 
                          display: 'block', 
                          color: '#C4B5FD', 
                          fontWeight: 'bold', 
                          marginBottom: '8px'
                        }}>
                          Price ($) *
                        </label>
                        <input
                          type="number"
                          value={newSong.price}
                          onChange={(e) => setNewSong(prev => ({ ...prev, price: e.target.value }))}
                          placeholder="15.00"
                          min="0.50"
                          step="0.50"
                          style={inputStyle}
                        />
                      </div>
                      
                      <button
                        onClick={addSong}
                        style={{
                          backgroundColor: '#10B981',
                          color: 'white',
                          fontWeight: 'bold',
                          padding: '18px 24px',
                          borderRadius: '16px',
                          border: '4px solid #059669',
                          fontSize: '16px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 8px rgba(16, 185, 129, 0.3)',
                          minWidth: isMobile ? '100%' : 'auto',
                          marginTop: isMobile ? '16px' : '0'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#059669';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = '#10B981';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        ➕ Add Song
                      </button>
                    </div>
                    
                    <div style={{
                      backgroundColor: '#4B5563',
                      borderRadius: '12px',
                      padding: '16px',
                      marginTop: '20px'
                    }}>
                      <h4 style={{ 
                        color: '#F9FAFB', 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        margin: '0 0 8px 0'
                      }}>
                        💡 Pricing Tips:
                      </h4>
                      <ul style={{ 
                        color: '#D1D5DB', 
                        fontSize: '14px', 
                        margin: 0, 
                        paddingLeft: '20px',
                        lineHeight: '1.5'
                      }}>
                        <li>Popular songs: $10-20 (high demand)</li>
                        <li>Regular repertoire: $5-15 (your standards)</li>
                        <li>Special requests: $20+ (challenging songs)</li>
                        <li>Minimum recommended: $2.50 (platform fees apply)</li>
                      </ul>
                    </div>
                  </div>

                  {formData.songs.length > 0 ? (
                    <div>
                      <h3 style={{ 
                        color: '#C4B5FD', 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        margin: '0 0 24px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span>🎵</span>
                        Your Songs ({formData.songs.length})
                      </h3>
                      
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: isMobile 
                          ? '1fr' 
                          : 'repeat(auto-fit, minmax(320px, 1fr))',
                        gap: '20px'
                      }}>
                        {formData.songs.map((song) => (
                          <div 
                            key={song.id} 
                            className="song-card"
                            style={{
                              backgroundColor: '#374151',
                              border: '4px solid #4B5563',
                              borderRadius: '20px',
                              padding: '24px',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            <div style={{ marginBottom: '16px' }}>
                              <h4 style={{ 
                                color: 'white', 
                                fontSize: '20px', 
                                fontWeight: 'bold', 
                                margin: '0 0 8px 0'
                              }}>
                                {song.name}
                              </h4>
                              <p style={{ 
                                color: '#C4B5FD', 
                                fontSize: '16px', 
                                margin: '0 0 12px 0',
                                fontStyle: 'italic'
                              }}>
                                by {song.artist}
                              </p>
                              <p style={{ 
                                color: '#10B981', 
                                fontSize: '24px', 
                                fontWeight: 'bold', 
                                margin: 0
                              }}>
                                ${song.price}
                              </p>
                            </div>
                            
                            <button
                              onClick={() => removeSong(song.id)}
                              style={{
                                backgroundColor: '#EF4444',
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '8px 16px',
                                borderRadius: '10px',
                                border: '2px solid #DC2626',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                width: '100%'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#DC2626';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = '#EF4444';
                              }}
                            >
                              🗑️ Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '60px 20px',
                      backgroundColor: '#374151',
                      borderRadius: '20px',
                      border: '4px dashed #6B7280'
                    }}>
                      <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎵</div>
                      <h3 style={{ 
                        color: '#D1D5DB', 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        margin: '0 0 12px 0'
                      }}>
                        No songs added yet
                      </h3>
                      <p style={{ 
                        color: '#9CA3AF', 
                        fontSize: '18px', 
                        margin: 0
                      }}>
                        Add your first song above to get started!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '40px',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '20px' : '0'
            }}>
              <button
                onClick={() => setCurrentPhase(1)}
                style={{
                  backgroundColor: '#6B7280',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '16px 32px',
                  borderRadius: '16px',
                  border: '4px solid #4B5563',
                  fontSize: '18px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: isMobile ? '100%' : 'auto'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#4B5563';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#6B7280';
                }}
              >
                ← Back to Profile Setup
              </button>
              
              <div style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                {formData.songs.length > 0 && (
                  <button
                    onClick={() => validatePhase2() && setCurrentPhase(3)}
                    style={{
                      ...buttonStyle,
                      backgroundColor: '#F59E0B',
                      borderColor: '#D97706',
                      fontSize: '20px',
                      padding: '20px 40px',
                      boxShadow: '0 15px 30px rgba(245, 158, 11, 0.4)',
                      minWidth: isMobile ? '100%' : 'auto'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#D97706';
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#F59E0B';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    <span>📱</span>
                    Next: Contact Info
                  </button>
                )}
                
                {formData.songs.length === 0 && (
                  <button
                    onClick={() => setCurrentPhase(3)}
                    style={{
                      backgroundColor: '#6B7280',
                      color: 'white',
                      fontWeight: 'bold',
                      padding: '16px 32px',
                      borderRadius: '16px',
                      border: '4px solid #4B5563',
                      fontSize: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      minWidth: isMobile ? '100%' : 'auto'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#4B5563';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#6B7280';
                    }}
                  >
                    Skip for now - I'll add songs later
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ✅ NEW: Phase 3 - Contact Info Only */}
        {currentPhase === 3 && (
          <div className="slide-in">
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h2 style={{ 
                fontSize: isMobile ? '32px' : '48px', 
                fontWeight: 'bold', 
                margin: '0 0 16px 0',
                background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Contact Information 📱
              </h2>
              <p style={{ 
                fontSize: isMobile ? '16px' : '24px', 
                color: '#9CA3AF',
                fontWeight: '500',
                margin: 0
              }}>
                Add your phone number to get notifications - enter it the way you know it!
              </p>
            </div>

            {selectedChar && (
              <div style={cardStyle}>
                <div style={{
                  padding: '32px',
                  borderBottom: '4px solid #6D28D9',
                  backgroundColor: '#581C87'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '20px',
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <div style={{
                      width: isMobile ? '80px' : '120px',
                      height: isMobile ? '80px' : '120px',
                      borderRadius: '50%',
                      backgroundColor: selectedChar.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '4px solid white',
                      flexShrink: 0,
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={selectedChar.image} 
                        alt={selectedChar.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        imageRendering: 'crisp-edges',
                        transform: 'translateZ(0)'
                        }}
                      />
                    </div>
                    
                    <div style={{
                      backgroundColor: '#374151',
                      borderRadius: '20px',
                      padding: '20px',
                      flex: 1,
                      position: 'relative',
                      border: '3px solid #4B5563'
                    }}>
                      {!isMobile && (
                        <div style={{
                          position: 'absolute',
                          left: '-12px',
                          top: '24px',
                          width: 0,
                          height: 0,
                          borderTop: '12px solid transparent',
                          borderBottom: '12px solid transparent',
                          borderRight: '12px solid #374151'
                        }}></div>
                      )}
                      
                      <p style={{ 
                        color: 'white', 
                        fontSize: isMobile ? '16px' : '18px',
                        margin: 0,
                        lineHeight: '1.5',
                        fontWeight: '500'
                      }}>
                        <TypewriterText 
                          text={selectedChar.messages.phoneCollection} 
                          speed={30}
                          key={`phoneCollection-${selectedCharacter}`}
                        />
                      </p>
                    </div>
                  </div>
                </div>
                
                <div style={{ padding: isMobile ? '24px' : '40px' }}>
                  {/* Benefits Section */}
                  <div style={{
                    backgroundColor: '#1E3A8A',
                    borderRadius: '20px',
                    padding: '24px',
                    border: '4px solid #3B82F6',
                    marginBottom: '32px'
                  }}>
                    <h3 style={{ 
                      fontSize: '20px', 
                      fontWeight: 'bold', 
                      margin: '0 0 16px 0',
                      color: '#93C5FD',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <span>📱</span>
                      Why add your phone number?
                    </h3>
                    <ul style={{ 
                      color: '#BFDBFE', 
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      margin: 0,
                      paddingLeft: '20px'
                    }}>
                      <li>Get instant SMS alerts when fans request songs</li>
                      <li>Never miss a paid song request</li>
                      <li>We'll verify it later to activate notifications</li>
                      <li>You can always add it later in your dashboard</li>
                    </ul>
                  </div>

                  {/* ✅ NEW: Country Selection + Local Number Input */}
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '20px',
                    padding: '32px',
                    border: '4px solid #4B5563'
                  }}>
                    <h3 style={{ 
                      color: '#C4B5FD', 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      margin: '0 0 24px 0'
                    }}>
                      Add Your Phone Number (Optional)
                    </h3>
                    
                    {/* Country Selection */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ 
                        display: 'block', 
                        color: '#C4B5FD', 
                        fontWeight: 'bold', 
                        fontSize: '18px', 
                        marginBottom: '12px'
                      }}>
                        Country
                      </label>
                      <select
                        value={phoneInput.country}
                        onChange={(e) => handlePhoneInputChange('country', e.target.value)}
                        style={{
                          ...inputStyle,
                          cursor: 'pointer'
                        }}
                      >
                        {Object.entries(countries).map(([code, country]) => (
                          <option 
                            key={code} 
                            value={code} 
                            className="country-option"
                            style={{ 
                              backgroundColor: '#374151', 
                              color: 'white',
                              padding: '8px'
                            }}
                          >
                            {country.flag} {country.name} ({country.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Phone Number Input */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ 
                        display: 'block', 
                        color: '#C4B5FD', 
                        fontWeight: 'bold', 
                        fontSize: '18px', 
                        marginBottom: '12px'
                      }}>
                        Phone Number
                      </label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        {/* Country Code Display */}
                        <div style={{
                          backgroundColor: '#4B5563',
                          border: '4px solid #6B7280',
                          borderRadius: '16px',
                          padding: '16px 20px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#E5E7EB',
                          minWidth: '100px',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>{selectedCountry.flag}</span>
                          <span>{selectedCountry.code}</span>
                        </div>
                        
                        {/* Local Number Input */}
                        <input
                          type="tel"
                          value={phoneInput.localNumber}
                          onChange={(e) => handlePhoneInputChange('localNumber', e.target.value)}
                          placeholder={selectedCountry.placeholder}
                          style={{
                            ...inputStyle,
                            flex: 1
                          }}
                        />
                      </div>
                      
                      {/* Format Example */}
                      <div style={{
                        backgroundColor: '#1F2937',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        border: '2px solid #374151'
                      }}>
                        <p style={{ 
                          color: '#9CA3AF', 
                          fontSize: '14px', 
                          margin: 0,
                          fontStyle: 'italic'
                        }}>
                          📝 Format: {selectedCountry.format} 
                          <br />
                          💡 Example: {selectedCountry.example}
                          {phoneInput.localNumber && (
                            <>
                              <br />
                              ✅ Will be saved as: {formData.phoneNumber}
                            </>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Validation Message */}
                    {phoneInput.localNumber && !validateLocalPhoneNumber(phoneInput.country, phoneInput.localNumber) && (
                      <div style={{
                        backgroundColor: '#7F1D1D',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        border: '2px solid #DC2626',
                        marginBottom: '16px'
                      }}>
                        <p style={{ 
                          color: '#FCA5A5', 
                          fontSize: '14px', 
                          margin: 0,
                          fontWeight: '500'
                        }}>
                          ❌ Please enter a valid {selectedCountry.name} phone number
                        </p>
                      </div>
                    )}

                    {/* Success Message */}
                    {phoneInput.localNumber && validateLocalPhoneNumber(phoneInput.country, phoneInput.localNumber) && (
                      <div style={{
                        backgroundColor: '#14532D',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        border: '2px solid #10B981',
                        marginBottom: '16px'
                      }}>
                        <p style={{ 
                          color: '#86EFAC', 
                          fontSize: '14px', 
                          margin: 0,
                          fontWeight: '500'
                        }}>
                          ✅ Phone number looks good! Will be stored as: {formData.phoneNumber}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Phase 3 Navigation */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '40px',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '20px' : '0'
            }}>
              <button
                onClick={() => setCurrentPhase(2)}
                style={{
                  backgroundColor: '#6B7280',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '16px 32px',
                  borderRadius: '16px',
                  border: '4px solid #4B5563',
                  fontSize: '18px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: isMobile ? '100%' : 'auto'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#4B5563';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#6B7280';
                }}
              >
                ← Back to Songs
              </button>
              
              <button
                onClick={handleComplete}
                disabled={isSubmitting}
                style={{
                  backgroundColor: isSubmitting ? '#6B7280' : '#059669',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '20px 40px',
                  borderRadius: '16px',
                  border: '4px solid #047857',
                  fontSize: '20px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: isMobile ? '100%' : 'auto',
                  boxShadow: '0 15px 30px rgba(5, 150, 105, 0.4)',
                  opacity: isSubmitting ? 0.7 : 1
                }}
                onMouseOver={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = '#047857';
                    e.target.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = '#059669';
                    e.target.style.transform = 'scale(1)';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <span className="pulse">⏳</span>
                    Creating your account...
                  </>
                ) : (
                  <>
                    <span>🎸</span>
                    Complete Setup & Start Earning!
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ✅ NEW: Phase 4 - Verification */}
        {currentPhase === 4 && (
          <div className="slide-in">
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h2 style={{ 
                fontSize: isMobile ? '32px' : '48px', 
                fontWeight: 'bold', 
                margin: '0 0 16px 0',
                background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Verification & Security 🔐
              </h2>
              <p style={{ 
                fontSize: isMobile ? '16px' : '24px', 
                color: '#9CA3AF',
                fontWeight: '500',
                margin: 0
              }}>
                Verify your contact details to unlock all features!
              </p>
            </div>

            {/* ✅ Phase 4: Verification Section */}
            <div style={{
              backgroundColor: '#1F2937',
              borderRadius: '20px',
              padding: '32px',
              border: '4px solid #7C3AED',
              marginBottom: '32px'
            }}>
              <h3 style={{ 
                color: '#C4B5FD', 
                fontSize: '24px', 
                fontWeight: 'bold', 
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span>📱</span>
                Phone Verification
              </h3>
              <p style={{ 
                color: '#D1D5DB', 
                fontSize: '16px', 
                margin: '0 0 24px 0',
                lineHeight: '1.5'
              }}>
                Verify your phone number to receive SMS alerts when fans request songs. This ensures you never miss an opportunity to earn!
              </p>

              <div style={{
                backgroundColor: '#1F2937',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '16px'
              }}>
                <p style={{ color: '#9CA3AF', fontSize: '14px', margin: '0 0 16px 0' }}>
                  <strong>Phone Number:</strong> {formData.phoneNumber || 'Enter phone number above'}
                </p>
                
                {!verificationState.phoneVerified && (
                  <>
                    {!verificationState.phoneCodeSent ? (
                      <button
                        onClick={sendPhoneVerification}
                        disabled={verificationState.isVerifying || !formData.phoneNumber.trim()}
                        style={{
                          width: '100%',
                          backgroundColor: verificationState.isVerifying || !formData.phoneNumber.trim() ? '#6B7280' : '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          cursor: verificationState.isVerifying || !formData.phoneNumber.trim() ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {verificationState.isVerifying ? 'Sending...' : 'Send SMS Verification Code'}
                      </button>
                    ) : (
                      <div>
                        <p style={{ color: '#10B981', fontSize: '14px', margin: '0 0 12px 0' }}>
                          ✅ SMS code sent to {formData.phoneNumber}
                        </p>
                        <input
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={verificationState.phoneCode}
                          onChange={(e) => setVerificationState(prev => ({ ...prev, phoneCode: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #374151',
                            borderRadius: '8px',
                            backgroundColor: '#374151',
                            color: 'white',
                            fontSize: '16px',
                            marginBottom: '12px',
                            boxSizing: 'border-box'
                          }}
                        />
                        <button
                          onClick={verifyPhoneCode}
                          disabled={verificationState.isVerifying || !verificationState.phoneCode.trim()}
                          style={{
                            width: '100%',
                            backgroundColor: verificationState.isVerifying || !verificationState.phoneCode.trim() ? '#6B7280' : '#10B981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: verificationState.isVerifying || !verificationState.phoneCode.trim() ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {verificationState.isVerifying ? 'Verifying...' : 'Verify Code'}
                        </button>
                      </div>
                    )}
                  </>
                )}

                {verificationState.phoneVerified && (
                  <div style={{
                    backgroundColor: '#14532D',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '2px solid #10B981'
                  }}>
                    <p style={{ color: '#86EFAC', fontSize: '14px', margin: 0, fontWeight: '500' }}>
                      ✅ Phone verified! You'll receive SMS alerts for new song requests.
                    </p>
                  </div>
                )}
              </div>

              <div style={{
                backgroundColor: '#451A03',
                borderRadius: '12px',
                padding: '16px',
                border: '2px solid #D97706'
              }}>
                <p style={{ 
                  color: '#92400E', 
                  fontSize: '14px', 
                  margin: '0',
                  lineHeight: '1.5'
                }}>
                  <strong>Why verify your phone?</strong> Get instant SMS alerts when fans request songs so you never miss earning opportunities! You can skip this for now and complete it later in your dashboard.
                </p>
              </div>
            </div>

            {/* Phase 4 Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '48px',
              gap: '16px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <button
                onClick={() => setCurrentPhase(3)}
                style={{
                  backgroundColor: '#374151',
                  color: 'white',
                  border: '2px solid #6B7280',
                  borderRadius: '12px',
                  padding: '16px 32px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                ← Back to Contact Info
              </button>
              
              <div style={{
                display: 'flex',
                gap: '12px',
                width: isMobile ? '100%' : 'auto'
              }}>
                <button
                  onClick={handleComplete}
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: isSubmitting ? '#6B7280' : '#6B7280',
                    color: 'white',
                    border: '2px solid #9CA3AF',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    flex: isMobile ? 1 : 'auto'
                  }}
                >
                  Skip Verification & Complete
                </button>
                
                <button
                  onClick={handleComplete}
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: isSubmitting ? '#6B7280' : 'linear-gradient(135deg, #7C3AED, #A855F7)',
                    background: isSubmitting ? '#6B7280' : 'linear-gradient(135deg, #7C3AED, #A855F7)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px 32px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    flex: isMobile ? 1 : 'auto'
                  }}
                >
                  {isSubmitting ? 'Completing...' : 'Complete Setup →'}
                </button>
              </div>
            </div>
          </div>
        )}
        </>
      </div>
    </div>
  );
};

export default MusicianOnboarding;